import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { FontIcon } from '@gik/ui';
import { Product } from '@gik/core/models/gik/Product';
import SavedProductCard from './SavedProductCard';
import SavedProductsListSkeleton from './SavedProductsListSkeleton';
import { useUserStore } from '@gik/core/store/UserStore';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { useSavedProducts } from '@gik/api/users/savedProducts';
import { removeFavoriteProduct } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';

const blockName = 'saved-articles-list';

export interface SavedProductsListProps {
  items?: Array<Product>;
}

function SavedProductsList({ items }: SavedProductsListProps) {
  const bem = bemBlock(blockName);

  const userStore = useUserStore();
  const { data: products, error: productsError, mutate } = useSavedProducts(userStore.id, {
    initialData: items,
  });

  async function handleDelete(product: Product) {
    const newData = products.filter(item => item.id !== product.id);
    optimisticUpdate(newData, mutate, () => removeFavoriteProduct(userStore.id, product.id));
  }

  const isLoading = !products;
  const isError = isLoading && productsError;

  if (isLoading) return <SavedProductsListSkeleton />;
  if (isError) return <SavedProductsListSkeleton />;

  if (products?.length === 0) {
    return (
      <div className={bem(null, 'empty')}>
        <div className={bem('preview')} />
        <div className={bem('preview')}>
          <span className={bem('instructions')}>
            Click the <FontIcon name="heart-o" /> on Products to save them here.
          </span>
        </div>
        <div className={bem('preview')} />
      </div>
    );
  }

  return (
    <ul className={bem('items')}>
      {products?.map((item: Product, index) => (
        <SavedProductCard key={index} product={item} onDelete={handleDelete} />
      ))}
    </ul>
  );
}

export default withSuspense(SavedProductsList, SavedProductsListSkeleton);
